(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopHover = window.matchMedia('(hover: hover) and (min-width: 990px)');

  // Native details preserve keyboard and screen-reader behavior; this controller
  // adds delayed pointer opening and an interruptible closing transition.
  const navigation = new Map();
  const setupNavigation = () => {
    document.querySelectorAll('.np-nav-group').forEach(group => {
      if (navigation.has(group)) return;
      const summary = group.querySelector(':scope > summary');
      const panel = group.querySelector(':scope > .np-nav-panel');
      if (!summary || !panel) return;
      const state = { summary, panel, timer: null, animation: null, version: 0, closing: false };
      navigation.set(group, state);
      summary.setAttribute('aria-expanded', String(group.open));
      panel.querySelectorAll('a').forEach((link, index) => link.style.setProperty('--np-link-order', Math.min(index, 7)));
      const clear = () => { clearTimeout(state.timer); state.animation?.cancel(); state.version += 1; };
      const close = (restoreFocus = false) => {
        if (!group.open || state.closing) return;
        clear();
        state.closing = true;
        const version = state.version;
        summary.setAttribute('aria-expanded', 'false');
        if (restoreFocus) summary.focus();
        panel.inert = true;
        const finish = () => {
          if (state.version !== version) return;
          group.open = false;
          panel.inert = false;
          state.closing = false;
        };
        if (reduced.matches || !panel.animate) { finish(); return; }
        state.animation = panel.animate([{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(-6px)'}],{duration:140,easing:'ease-in',fill:'forwards'});
        state.animation.finished.then(finish, () => {});
      };
      const open = () => {
        clearTimeout(state.timer);
        if (group.open && !state.closing) return;
        clear();
        state.closing = false;
        navigation.forEach((other, element) => { if (element !== group) other.close(); });
        group.open = true;
        panel.inert = false;
        summary.setAttribute('aria-expanded', 'true');
        if (!reduced.matches && panel.animate) {
          state.animation = panel.animate([{opacity:0,transform:'translateY(9px)'},{opacity:1,transform:'translateY(0)'}],{duration:260,easing:'cubic-bezier(.2,.7,.2,1)'});
        }
      };
      state.open = open;
      state.close = close;
      summary.addEventListener('click', event => { event.preventDefault(); group.open && !state.closing ? close() : open(); });
      group.addEventListener('pointerenter', event => {
        if (!desktopHover.matches || event.pointerType === 'touch') return;
        clearTimeout(state.timer);
        state.timer = setTimeout(open, 130);
      });
      group.addEventListener('pointerleave', event => {
        if (!desktopHover.matches || event.pointerType === 'touch') return;
        clearTimeout(state.timer);
        if (!group.contains(document.activeElement)) state.timer = setTimeout(() => close(), 180);
      });
      group.addEventListener('focusout', event => {
        if (!group.contains(event.relatedTarget)) {
          clearTimeout(state.timer);
          state.timer = setTimeout(() => close(), 180);
        }
      });
      group.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' && event.target === summary) {
          event.preventDefault(); open(); panel.querySelector('a')?.focus();
        }
      });
      group.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(group.open && !state.closing));
        if (!group.open) { panel.inert = false; state.closing = false; }
      });
    });
  };
  document.addEventListener('click', event => navigation.forEach((state, group) => { if (!group.contains(event.target)) state.close(); }));
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') navigation.forEach((state, group) => { if (group.open) state.close(group.contains(document.activeElement)); });
  });
  document.addEventListener('shopify:section:load', setupNavigation);
  setupNavigation();

  if (!customElements.get('np-category-rail')) {
    customElements.define('np-category-rail', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.track = this.querySelector('[data-np-category-track]');
        const prev = this.querySelector('[data-np-categories-prev]');
        const next = this.querySelector('[data-np-categories-next]');
        if (!this.track) return;
        const update = () => {
          const maximum = this.track.scrollWidth - this.track.clientWidth;
          this.dataset.overflow = String(maximum > 2);
          if (prev) prev.disabled = this.track.scrollLeft < 2;
          if (next) next.disabled = this.track.scrollLeft >= maximum - 2;
        };
        const move = direction => this.track.scrollBy({left:direction*this.track.clientWidth*.75,behavior:reduced.matches?'auto':'smooth'});
        prev?.addEventListener('click', () => move(-1), {signal});
        next?.addEventListener('click', () => move(1), {signal});
        this.track.addEventListener('scroll', update, {passive:true,signal});
        this.track.addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          const links = [...this.track.querySelectorAll('a')];
          const current = links.indexOf(document.activeElement);
          if (current < 0) return;
          let index = current + (event.key === 'ArrowRight' ? 1 : -1);
          if (event.key === 'Home') index = 0;
          if (event.key === 'End') index = links.length - 1;
          index = Math.max(0,Math.min(index,links.length-1));
          event.preventDefault();
          links[index].focus({preventScroll:true});
          links[index].scrollIntoView({block:'nearest',inline:'nearest',behavior:reduced.matches?'auto':'smooth'});
        },{signal});
        this.resize = new ResizeObserver(update);
        this.resize.observe(this.track);
        if (reduced.matches) this.classList.add('is-visible');
        else {
          this.reveal = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
              this.classList.add('is-visible'); this.reveal.disconnect();
            }
          },{threshold:.15});
          this.reveal.observe(this);
        }
        update();
      }
      disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); this.reveal?.disconnect(); this.controller=null; }
    });
  }

  if (!customElements.get('np-brand-banner')) {
    customElements.define('np-brand-banner', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.slides = [...this.querySelectorAll('[data-np-banner-slide]')];
        this.dots = [...this.querySelectorAll('[data-np-banner-index]')];
        this.play = this.querySelector('[data-np-banner-play]');
        this.current = 0;
        this.version = 0;
        this.animations = [];
        this.hovered = false;
        this.focused = false;
        this.visible = true;
        this.paused = this.dataset.autoplay !== 'true' || reduced.matches;
        this.querySelector('[data-np-banner-prev]')?.addEventListener('click', () => this.show(this.current-1,true),{signal});
        this.querySelector('[data-np-banner-next]')?.addEventListener('click', () => this.show(this.current+1,true),{signal});
        this.dots.forEach((button,index) => button.addEventListener('click', () => this.show(index,true),{signal}));
        this.play?.addEventListener('click', () => { this.paused=!this.paused; this.paintPlay(); this.schedule(); },{signal});
        this.addEventListener('mouseenter', () => {this.hovered=true;this.schedule();},{signal});
        this.addEventListener('mouseleave', () => {this.hovered=false;this.schedule();},{signal});
        this.addEventListener('focusin', () => {this.focused=true;this.schedule();},{signal});
        this.addEventListener('focusout', event => {if(!this.contains(event.relatedTarget)){this.focused=false;this.schedule();}},{signal});
        this.addEventListener('keydown', event => {
          if (!event.target.matches('button')) return;
          if (event.key==='ArrowLeft'||event.key==='ArrowRight') {event.preventDefault();this.show(this.current+(event.key==='ArrowRight'?1:-1),true);}
        },{signal});
        const track=this.querySelector('[data-np-banner-track]');
        let touch=null;
        track?.addEventListener('pointerdown',event=>{if(event.pointerType==='touch')touch={x:event.clientX,y:event.clientY};},{signal});
        track?.addEventListener('pointerup',event=>{
          if(!touch)return;
          const x=event.clientX-touch.x,y=event.clientY-touch.y;touch=null;
          if(Math.abs(x)>45&&Math.abs(x)>Math.abs(y)*1.4)this.show(this.current+(x<0?1:-1),true);
        },{signal});
        track?.addEventListener('pointercancel',()=>{touch=null;},{signal});
        document.addEventListener('visibilitychange',()=>this.schedule(),{signal});
        reduced.addEventListener('change',()=>{if(reduced.matches)this.paused=true;this.paintPlay();this.schedule();},{signal});
        this.visibility=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.schedule();},{threshold:.05});
        this.visibility.observe(this);
        this.paintPlay();
        this.schedule();
      }
      paintPlay() {
        if(!this.play)return;
        this.play.disabled=reduced.matches;
        this.play.setAttribute('aria-label',this.paused?'Resume banner rotation':'Pause banner rotation');
        this.play.setAttribute('aria-pressed',String(this.paused));
        this.play.querySelector('[data-np-play-icon]').textContent=this.paused?'▷':'Ⅱ';
      }
      schedule() {
        clearTimeout(this.timer);
        const active=!this.paused&&!this.hovered&&!this.focused&&this.visible&&!document.hidden&&!reduced.matches&&this.slides.length>1;
        this.dataset.playing=String(active);
        if(active)this.timer=setTimeout(()=>this.show(this.current+1,false),6500);
      }
      show(requested,manual) {
        const next=(requested+this.slides.length)%this.slides.length;
        if(next===this.current){this.schedule();return;}
        this.animations.forEach(animation=>animation.cancel());
        const previous=this.current;
        this.current=next;
        const version=++this.version;
        this.slides.forEach((slide,index)=>{
          slide.hidden=index!==previous&&index!==next;
          slide.inert=index!==next;
          slide.dataset.active=String(index===next);
        });
        this.dots.forEach((button,index)=>button.setAttribute('aria-pressed',String(index===next)));
        const finish=()=>{if(this.version===version)this.slides.forEach((slide,index)=>{slide.hidden=index!==next;});};
        if(reduced.matches||!this.slides[next].animate)finish();
        else {
          this.animations=[
            this.slides[previous].animate([{opacity:1},{opacity:0}],{duration:380,easing:'ease-in-out'}),
            this.slides[next].animate([{opacity:0},{opacity:1}],{duration:480,easing:'ease-out'})
          ];
          Promise.all(this.animations.map(animation=>animation.finished)).then(finish,()=>{});
        }
        if(manual)this.querySelector('[data-np-banner-status]').textContent=`Banner ${next+1} of ${this.slides.length}`;
        this.schedule();
      }
      disconnectedCallback(){this.controller?.abort();this.visibility?.disconnect();clearTimeout(this.timer);this.animations?.forEach(animation=>animation.cancel());this.controller=null;}
    });
  }
})();
